import { useEffect, useState } from "react";

/** Tracks the user's reduced-motion preference (re-renders on change). */
export function usePrefersReducedMotion() {
  const [reduced, setReduced] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    setReduced(mq.matches);
    const on = () => setReduced(mq.matches);
    mq.addEventListener("change", on);
    return () => mq.removeEventListener("change", on);
  }, []);
  return reduced;
}

/** Animates a number from 0 → `value` on mount (ease-out), then formats it.
 *  Respects prefers-reduced-motion by snapping straight to the final value. */
export function CountUp({
  value,
  decimals = 0,
  format,
}: { value: number; decimals?: number; format?: (n: number) => string }) {
  const reduced = usePrefersReducedMotion();
  const [n, setN] = useState(reduced ? value : 0);
  useEffect(() => {
    if (reduced) {
      setN(value);
      return;
    }
    let raf = 0;
    const start = performance.now();
    const dur = 850;
    const tick = (now: number) => {
      const t = Math.min(1, (now - start) / dur);
      const e = 1 - (1 - t) ** 3;
      setN(value * e);
      if (t < 1) raf = requestAnimationFrame(tick);
      else setN(value);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [value, reduced]);
  return <>{countUpText(n, decimals, format)}</>;
}

/**
 * What `CountUp` prints for one frame of its animation.
 *
 * A supplied `format` owns presentation entirely, so it gets the value
 * UNROUNDED. Rounding to `decimals` first threw away exactly what the
 * magnitude-aware formatters need in order to pick a unit: the map's hero card
 * passes `decimals={1}` alongside `fmtFromMillions`, so a market with 0.00131M
 * visitors was rounded to 0 and published as "0" — where the formatter on its
 * own renders it "1.3 K". Every source market under ~50,000 visitors read as
 * zero on that panel, on both deployments.
 *
 * `decimals` still governs the default rendering, which has no formatter to
 * defer to. Split out from the component so that contract is testable without
 * an animation frame — under `renderToStaticMarkup` the effect never runs and
 * every count-up renders its first frame, which is zero.
 */
export function countUpText(n: number, decimals: number, format?: (v: number) => string): string {
  if (format) return format(n);
  const rounded = Number(n.toFixed(decimals));
  return rounded.toLocaleString(undefined, { minimumFractionDigits: decimals, maximumFractionDigits: decimals });
}
